import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/Badges";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function Campaigns() {
  const { user } = useAuth();
  const isAdmin = user?.role === "crm_manager";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (c) => {
    if (!window.confirm(`Permanently delete campaign "${c.name}"? KPI definitions and history attached to it will be orphaned.`)) return;
    try {
      await api.delete(`/campaigns/${c.id}`);
      toast.success("Campaign deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filtered = rows.filter(
    (r) =>
      !q ||
      r.name.toLowerCase().includes(q.toLowerCase()) ||
      (r.client_name || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="campaigns-page">
      <div>
        <div className="mptg-overline">Operations</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Campaigns</h1>
        <p className="text-slate-500 mt-1">{rows.length} campaign{rows.length === 1 ? "" : "s"}</p>
      </div>

      <div className="mptg-card">
        <Input
          placeholder="Search campaign or client…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="campaign-search"
        />
      </div>

      <div className="mptg-card p-0 overflow-hidden">
        <table className="w-full mptg-table">
          <thead>
            <tr>
              <th>Campaign</th><th>Client</th><th>Type</th><th>Branch</th><th>PM</th><th>Status</th>
              {isAdmin && <th className="text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isAdmin ? 7 : 6} className="py-8 text-center text-slate-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={isAdmin ? 7 : 6} className="py-8 text-center text-slate-400">No campaigns.</td></tr>
            ) : (
              filtered.map((c) => (
                <tr key={c.id} data-testid={`campaign-row-${c.id}`}>
                  <td><Link className="font-semibold text-slate-800 hover:text-[#2E86C1]" to={`/campaigns/${c.id}`}>{c.name}</Link></td>
                  <td><Link to={`/clients/${c.client_id}`} className="text-[#2E86C1] hover:underline">{c.client_name}</Link></td>
                  <td className="text-slate-600">{c.type}</td>
                  <td className="text-slate-600">{c.branch_label || c.branch || "-"}</td>
                  <td className="text-slate-600">{c.process_manager_name || "-"}</td>
                  <td><StatusBadge status={c.status} /></td>
                  {isAdmin && (
                    <td className="text-right whitespace-nowrap">
                      <button
                        onClick={() => del(c)}
                        className="text-[#C0392B] hover:underline text-xs inline-flex items-center gap-1"
                        data-testid={`delete-campaign-${c.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
